import { useState, useMemo } from "react";
import { Button, Form, Input, notification, Upload, Select } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { post, uploadFile } from "../../../services";
import ReactQuill from "react-quill";
import { ReactQuillOptions } from "../../../utils/react-quill-options";

const EventForm = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const openNotification = (type, message, description) => {
    notification[type]({
      message,
      description,
    });
  };

  const uploadProps = useMemo(
    () => ({
      beforeUpload: (file) => {
        const isImage = file.type.startsWith("image/");
        const isValidSize = file.size <= 5 * 1024 * 1024;
        const hasSpaces = /\s/.test(file.name);

        if (!isImage) {
          openNotification("error", "Invalid File", "Please upload an image file.");
          return Upload.LIST_IGNORE;
        }
        if (!isValidSize) {
          openNotification("error", "File Too Large", "File size exceeds 5MB.");
          return Upload.LIST_IGNORE;
        }
        if (hasSpaces) {
          openNotification(
            "error",
            "Invalid File Name",
            "Remove spaces from file name before uploading."
          );
          return Upload.LIST_IGNORE;
        }

        setFileList([file]);
        return false;
      },
      onRemove: () => setFileList([]),
      fileList,
      multiple: false,
      listType: "picture",
    }),
    [fileList]
  );

  // Upload file handler
  const uploadFileHandler = async (file) => {
    try {
      setIsUploading(true);
      const response = await uploadFile("/getSignUrlForUpload", { file });
      if (response?.data?.publicUrl) {
        return response.data.publicUrl;
      } else {
        throw new Error("Upload failed. Please try again.");
      }
    } catch (error) {
      openNotification("error", "Upload Error", error.message || "Something went wrong.");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // Submit handler with validations
  const onFinish = async (values) => {
    if (fileList.length === 0) {
      openNotification("warning", "Missing Image", "Please upload a profile image.");
      return;
    }

    if (isUploading) {
      openNotification("info", "Please Wait", "File is still uploading.");
      return;
    }

    setLoader(true);
    try {
      openNotification("info", "Uploading", "File upload may take a few seconds...");

      const uploadedUrl = await uploadFileHandler(fileList[0]);
      if (!uploadedUrl) {
        setLoader(false);
        return;
      }

      const payload = {
        section: values.section,
        fullName: values.fullName,
        title: values.title || "",
        languagesSpoken: values.languagesSpoken || "",
        description: values.description || "",
        joiningDate: values.joiningDate || "",
        imageUrl: uploadedUrl ,
      };

      await post(`/team`, payload);

      openNotification("success", "Success", "Team member added successfully!");
      navigate("/our-team");
    } catch (error) {
      openNotification("error", "Submission Failed", error.message || "Something went wrong.");
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <div className="header">
        <h3>Add Team Member</h3>
      </div>
      <div className="form_container">
        <Form layout="vertical" onFinish={onFinish}>
          {/* Section */}
          <Form.Item
            name="section"
            label="Section"
            rules={[{ required: true, message: "Please select a section." }]}
          >
            <Select
              placeholder="Select a section"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              <Select.Option value="Management Team">Management Team</Select.Option>
              <Select.Option value="Sales Team">Sales Team</Select.Option>
              <Select.Option value="Operations Team">Operations Team</Select.Option>
              <Select.Option value="Marketing Team">Marketing Team</Select.Option>
              <Select.Option value="Holiday Homes Team">Holiday Homes Team</Select.Option>
              <Select.Option value="Support Team">Support Team</Select.Option>
            </Select>
          </Form.Item>

          {/* Image */}
          <Form.Item
            name="image"
            label="Profile Image"
            required
            tooltip="Upload a profile image (max 5MB, no spaces)"
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
          </Form.Item>

          {/* Name */}
          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: "Please enter the name." }]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>

          {/* Title */}
          <Form.Item name="title" label="Title">
            <Input placeholder="Enter title" />
          </Form.Item>

          {/* Language */}
          <Form.Item name="languagesSpoken" label="Languages Spoken">
            <Input placeholder="Enter languages" />
          </Form.Item>

          {/* Description */}
          <Form.Item name="description" label="Description">
            <ReactQuill
              className="my-editor"
              theme="snow"
              modules={ReactQuillOptions}
              placeholder="Write description..."
            />
          </Form.Item>

          {/* Date */}
          <Form.Item name="joiningDate" label="Joining Date">
            <Input type="date" />
          </Form.Item>

          {/* Submit */}
          <Form.Item>
            <div className="form_submit">
              <Button
                className="back_button"
                onClick={() => navigate("/our-team")}
                disabled={loader}
              >
                Back
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loader}
                disabled={isUploading}
              >
                {loader ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default EventForm;
